// ApiExplorer.tsx
// Shared component for exploring and testing API endpoints

import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Play,
  Download,
  X,
  CheckCircle2,
  XCircle,
  Loader2,
  Calendar,
  FileText,
  AlertCircle
} from 'lucide-react';
import { ApiConfig, ApiSourceId, TestResult, TestStatus } from '../types/apiSources';
import {
  runApiTests,
  pollUntilComplete,
  getTestResults,
  exportData
} from '../utils/apiSourcesService';
import { useDemoMode } from '../contexts/DemoModeContext';
import { Badge } from './ui/badge';
import { Button } from '@/components/ui/button';

interface ApiExplorerProps {
  apiConfig: ApiConfig;
}

export default function ApiExplorer({ apiConfig }: ApiExplorerProps) {
  const { isDemoMode } = useDemoMode();
  const demoNameMap: Record<string, string> = {
    aquarius: 'API 1',
    jigsaw: 'API 2',
    optifleet: 'API 3',
    qargo: 'API 4',
  };
  const demoBaseUrlMap: Record<string, string> = {
    aquarius: 'https://api.demo.local/api-1',
    jigsaw: 'https://api.demo.local/api-2',
    optifleet: 'https://api.demo.local/api-3',
    qargo: 'https://api.demo.local/api-4',
  };
  // State
  const [selectedEndpoints, setSelectedEndpoints] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [recordLimit, setRecordLimit] = useState<number>(100);
  
  // Test execution state
  const [isRunning, setIsRunning] = useState(false);
  const [testStatus, setTestStatus] = useState<TestStatus | null>(null);
  const [testResults, setTestResults] = useState<TestResult[] | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  
  // Results filtering
  const [resultsFilter, setResultsFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [resultsSearch, setResultsSearch] = useState<string>('');

  // Initialize default date range (last 7 days)
  React.useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  // Flatten all endpoints for easy access
  const allEndpoints = useMemo(() => {
    return apiConfig.categories.flatMap(cat => cat.endpoints);
  }, [apiConfig]);

  // Get working endpoints only
  const workingEndpoints = useMemo(() => {
    return allEndpoints.filter(e => e.status === 'working');
  }, [allEndpoints]);

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Toggle endpoint selection
  const toggleEndpoint = (endpointId: string) => {
    setSelectedEndpoints(prev => {
      const next = new Set(prev);
      if (next.has(endpointId)) {
        next.delete(endpointId);
      } else {
        next.add(endpointId);
      }
      return next;
    });
  };

  // Select all working endpoints
  const selectAll = () => {
    setSelectedEndpoints(new Set(workingEndpoints.map(e => e.id)));
  };

  // Deselect all
  const selectNone = () => {
    setSelectedEndpoints(new Set());
  };

  // Add log message
  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  // Run tests
  const handleRunTests = async () => {
    if (selectedEndpoints.size === 0) {
      alert('Please select at least one endpoint to test');
      return;
    }

    setIsRunning(true);
    setTestResults(null);
    setShowResults(false);
    setLogs([]);
    addLog(`Starting tests for ${selectedEndpoints.size} endpoints...`);

    try {
      // Start tests
      const startResult = await runApiTests(
        apiConfig.id as ApiSourceId,
        Array.from(selectedEndpoints),
        startDate,
        endDate,
        recordLimit
      );

      if (!startResult.success) {
        throw new Error(startResult.message);
      }

      addLog('Tests initiated successfully');
      addLog('Polling for status updates...');

      // Poll for completion
      const completed = await pollUntilComplete(
        apiConfig.id as ApiSourceId,
        (status) => {
          setTestStatus(status);
          if (status.currentEndpoint) {
            addLog(`Testing: ${status.currentEndpoint}`);
          }
        }
      );

      if (!completed) {
        throw new Error('Test polling timeout (10 minutes)');
      }

      addLog('Tests completed, fetching results...');

      // Get results
      const results = await getTestResults(apiConfig.id as ApiSourceId);
      
      if (!results.success) {
        throw new Error(results.error || 'Failed to fetch results');
      }

      setTestResults(results.results || []);
      addLog(`Results retrieved: ${results.totalTests} total, ${results.successCount} successful, ${results.failureCount} failed`);
      setShowResults(true);

    } catch (error) {
      console.error('Test execution error:', error);
      addLog(`ERROR: ${error instanceof Error ? error.message : 'Unknown error'}`);
      alert(`Test execution failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
      setTestStatus(null);
    }
  };

  // Export data
  const handleExportData = async () => {
    if (selectedEndpoints.size === 0) {
      alert('Please select at least one endpoint to export');
      return;
    }

    if (!startDate || !endDate) {
      alert('Please select a date range for export');
      return;
    }

    addLog(`Exporting data for ${selectedEndpoints.size} endpoints...`);

    try {
      const result = await exportData(
        apiConfig.id as ApiSourceId,
        Array.from(selectedEndpoints),
        startDate,
        endDate
      );

      if (result.success) {
        addLog(`Export successful: ${result.message}`);
        if (result.csvPath) {
          addLog(`CSV file: ${result.csvPath}`);
        }
        alert(result.message);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Export error:', error);
      addLog(`Export ERROR: ${error instanceof Error ? error.message : 'Unknown error'}`);
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Filter results
  const filteredResults = useMemo(() => {
    if (!testResults) return [];
    
    return testResults.filter(result => {
      // Filter by status
      if (resultsFilter === 'success' && !result.success) return false;
      if (resultsFilter === 'failed' && result.success) return false;
      
      // Filter by search
      if (resultsSearch) {
        const search = resultsSearch.toLowerCase();
        return (
          result.endpoint.toLowerCase().includes(search) ||
          result.method.toLowerCase().includes(search) ||
          result.error?.toLowerCase().includes(search)
        );
      }
      
      return true;
    });
  }, [testResults, resultsFilter, resultsSearch]);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ color: apiConfig.themeColor }}>
              {isDemoMode ? (demoNameMap[apiConfig.id] || apiConfig.name) : apiConfig.name}
            </h2>
            <p className="text-muted-foreground mb-3">{apiConfig.description}</p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Base URL:</span>
                <code className="bg-background px-2 py-1 rounded text-xs text-foreground border border-border">
                  {isDemoMode ? (demoBaseUrlMap[apiConfig.id] || apiConfig.baseUrl) : apiConfig.baseUrl}
                </code>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Auth:</span>
                <span className="font-medium text-foreground">{apiConfig.authMethod}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold" style={{ color: apiConfig.themeColor }}>
              {apiConfig.totalEndpoints}
            </div>
            <div className="text-sm text-muted-foreground">Total Endpoints</div>
            <div className="text-sm mt-1">
              <span className="text-success font-medium">{apiConfig.workingEndpoints} working</span>
              {' · '}
              <span className="text-destructive font-medium">{apiConfig.totalEndpoints - apiConfig.workingEndpoints} failed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-card border-b border-border p-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-1.5 bg-background border border-border rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <span className="text-muted-foreground">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-1.5 bg-background border border-border rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Record Limit */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Limit:</span>
            <input
              type="number"
              value={recordLimit}
              onChange={(e) => setRecordLimit(parseInt(e.target.value) || 100)}
              className="w-24 px-3 py-1.5 bg-background border border-border rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-ring"
              min="1"
              max="10000"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 ml-auto">
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="border border-accent"
              onClick={selectAll}
              disabled={isRunning}
            >
              All
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="border border-accent"
              onClick={selectNone}
              disabled={isRunning}
            >
              None
            </Button>
            <Button
              type="button"
              variant="default"
              size="md"
              loading={isRunning}
              onClick={handleRunTests}
              disabled={selectedEndpoints.size === 0}
              className="hover:opacity-90"
              style={{ backgroundColor: apiConfig.themeColor }}
            >
              <Play className="w-4 h-4" aria-hidden />
              Run Tests
            </Button>
            {testResults && (
              <Button
                type="button"
                variant="default"
                size="md"
                onClick={() => setShowResults(true)}
              >
                <FileText className="w-4 h-4" aria-hidden />
                View Results ({testResults.length})
              </Button>
            )}
          </div>
        </div>

        {/* Status Bar */}
        {(isRunning || testStatus) && (
          <div className="mt-3 p-3 bg-ring/10 border border-ring/20 rounded">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-ring" />
                <span className="text-sm font-medium text-ring">
                  {testStatus?.message || 'Running tests...'}
                </span>
                {testStatus?.currentEndpoint && (
                  <span className="text-sm text-ring">
                    ({testStatus.currentEndpoint})
                  </span>
                )}
              </div>
              <div className="text-sm text-ring">
                {selectedEndpoints.size} endpoint{selectedEndpoints.size !== 1 ? 's' : ''} selected
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Endpoint List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3">
          {apiConfig.categories.map((category) => {
            const isExpanded = expandedCategories.has(category.id);
            const categoryEndpoints = category.endpoints;
            const selectedInCategory = categoryEndpoints.filter(e => selectedEndpoints.has(e.id)).length;

            return (
              <div key={category.id} className="bg-card rounded-lg border border-border overflow-hidden">
                {/* Category Header */}
                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  onClick={() => toggleCategory(category.id)}
                  className="w-full justify-between px-4"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                    <span className="font-semibold text-foreground">{category.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {categoryEndpoints.length} endpoint{categoryEndpoints.length !== 1 ? 's' : ''}
                    </span>
                    {selectedInCategory > 0 && (
                      <Badge tone="neutral"
                            className="border-transparent bg-transparent text-white"
                            style={{ backgroundColor: apiConfig.themeColor }}>
                        {selectedInCategory} selected
                      </Badge>
                    )}
                  </div>
                </Button>

                {/* Category Endpoints */}
                {isExpanded && (
                  <div className="border-t border-border">
                    {categoryEndpoints.map((endpoint) => (
                      <div
                        key={endpoint.id}
                        className="px-4 py-3 border-b border-border last:border-b-0"
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedEndpoints.has(endpoint.id)}
                            onChange={() => toggleEndpoint(endpoint.id)}
                            disabled={endpoint.status === 'failed' || isRunning}
                            className="mt-1 w-4 h-4 rounded border-accent"
                            style={{ accentColor: apiConfig.themeColor }}
                          />

                          {/* Endpoint Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {/* Method Badge */}
                              <span className={`px-2 py-0.5 text-xs font-mono font-bold rounded ${
                                endpoint.method === 'GET' ? 'bg-ring/20 text-ring' :
                                endpoint.method === 'POST' ? 'bg-success/20 text-success' :
                                endpoint.method === 'PUT' ? 'bg-warning/20 text-warning' :
                                endpoint.method === 'DELETE' ? 'bg-destructive/20 text-destructive' :
                                'bg-accent/20 text-muted-foreground'
                              }`}>
                                {endpoint.method}
                              </span>

                              {/* Status Badge */}
                              {endpoint.status === 'working' ? (
                                <CheckCircle2 className="w-4 h-4 text-success" />
                              ) : (
                                <XCircle className="w-4 h-4 text-destructive" />
                              )}

                              {/* Path */}
                              <code className="text-sm font-mono text-foreground">
                                {endpoint.path}
                              </code>
                            </div>

                            {/* Description */}
                            <p className="text-sm text-muted-foreground mb-2">
                              {endpoint.description}
                            </p>

                            {/* Fields */}
                            {endpoint.fields && endpoint.fields.length > 0 && (
                              <details className="text-xs">
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                  {endpoint.fields.length} field{endpoint.fields.length !== 1 ? 's' : ''}
                                </summary>
                                <div className="mt-2 ml-4 space-y-1">
                                  {endpoint.fields.map((field, idx) => (
                                    <div key={idx} className="flex items-start gap-2">
                                      <code className="font-mono text-ring">{field.name}</code>
                                      <span className="text-muted-foreground">:</span>
                                      <span className="text-foreground">{field.type}</span>
                                      {field.description && (
                                        <>
                                          <span className="text-muted-foreground">-</span>
                                          <span className="text-muted-foreground">{field.description}</span>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}

                            {/* Tags */}
                            <div className="flex items-center gap-2 mt-2">
                              {endpoint.requiresDateRange && (
                                <span className="px-2 py-0.5 text-xs bg-brand/20 text-brand rounded">
                                  Date Range Required
                                </span>
                              )}
                              {endpoint.requiresParams && (
                                <span className="px-2 py-0.5 text-xs bg-warning/20 text-warning rounded">
                                  Parameters Required
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Logs Section */}
      {logs.length > 0 && (
        <div className="border-t border-border bg-background">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => setShowLogs(!showLogs)}
            className="w-full justify-between text-foreground hover:bg-card"
          >
            <span className="text-sm font-medium">Logs ({logs.length})</span>
            {showLogs ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
          {showLogs && (
            <div className="px-4 py-2 max-h-48 overflow-y-auto bg-background">
              {logs.map((log, idx) => (
                <div key={idx} className="text-xs text-foreground font-mono mb-1">
                  {log}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results Modal */}
      {showResults && testResults && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-6xl max-h-screen-90 flex flex-col border border-border">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-xl font-bold text-white">Test Results</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {testResults.length} total · {testResults.filter(r => r.success).length} success · {testResults.filter(r => !r.success).length} failed
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Filter Buttons */}
                <div className="flex items-center gap-1 border border-accent rounded">
                  <Button
                    type="button"
                    variant={resultsFilter === 'all' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setResultsFilter('all')}
                    className="text-sm text-foreground hover:bg-secondary"
                  >
                    All
                  </Button>
                  <Button
                    type="button"
                    variant={resultsFilter === 'success' ? 'success' : 'ghost'}
                    size="sm"
                    onClick={() => setResultsFilter('success')}
                    className="text-sm text-foreground hover:bg-secondary"
                  >
                    Success
                  </Button>
                  <Button
                    type="button"
                    variant={resultsFilter === 'failed' ? 'destructive' : 'ghost'}
                    size="sm"
                    onClick={() => setResultsFilter('failed')}
                    className="text-sm text-foreground hover:bg-secondary"
                  >
                    Failed
                  </Button>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowResults(false)}
                  aria-label="Close results"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="px-6 py-3 border-b border-border">
              <input
                type="text"
                placeholder="Search endpoints..."
                value={resultsSearch}
                onChange={(e) => setResultsSearch(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Results List */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {filteredResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-2 ${
                      result.success
                        ? 'border-success/30 bg-success/10'
                        : 'border-destructive/30 bg-destructive/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {result.success ? (
                            <CheckCircle2 className="w-5 h-5 text-success" />
                          ) : (
                            <XCircle className="w-5 h-5 text-destructive" />
                          )}
                          <span className="font-mono font-bold text-sm text-foreground">{result.method}</span>
                          <code className="text-sm text-foreground">{result.endpoint}</code>
                        </div>

                        {result.success ? (
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-muted-foreground">
                              Status: <span className="font-medium text-success">{result.statusCode}</span>
                            </span>
                            <span className="text-muted-foreground">
                              Time: <span className="font-medium text-white">{result.responseTime}ms</span>
                            </span>
                            {result.recordCount !== undefined && (
                              <span className="text-muted-foreground">
                                Records: <span className="font-medium text-white">{result.recordCount}</span>
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-destructive mt-2">
                            <strong>Error:</strong> {result.error || 'Unknown error'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {filteredResults.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                    <p>No results match your filter criteria</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-background">
              <Button
                type="button"
                variant="secondary"
                size="md"
                className="border border-accent"
                onClick={() => setShowResults(false)}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="default"
                size="md"
                onClick={handleExportData}
                className="hover:opacity-90"
                style={{ backgroundColor: apiConfig.themeColor }}
              >
                <Download className="w-4 h-4" aria-hidden />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
